import { inject, Injectable } from "@angular/core";
import { LimitOrderNavigationFacade } from "@modules/limit-orders/services/navigation.facade";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { AccountService } from "@modules/account/services/account.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TooltipService } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import {
	AmountType,
	CheckoutRouteName,
	PaymentMethod,
	CheckoutQueryParamName,
	CheckoutQueryParamsCollection
} from "@modules/checkout/entities/checkout.model";
import {
	LimitOrderQueryParamName,
	LimitOrderRouteName,
	OrdersQueryParamsCollection
} from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { catchError, take } from "rxjs/operators";
import { LegacyTradeEntryService } from "@modules/trade/helpers/legacy-trade-entry.service";
import { Modal, ModalService } from "@core-services/modal.service";
import { WalletSelectionModalComponent } from "@modules/main/components/tradable-to-view-item/tradable-to-wallet/wallet-selection-modal/wallet-selection-modal.component";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { OrderType } from "@gql/offer";
import { TaxesFacade } from "@modules/taxes/domain/+state/taxes.facade";
import { TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import {
	SelfCertificationModel,
	TaxesRouteNames,
	TaxesUserStatusModel,
	ToastMessages
} from "@modules/taxes/domain/entities/taxes.model";
import { of } from "rxjs";
import { Helper } from "@helpers/functions";
import { CRYPTO_TYPE, INDEX_TYPE, TAXES_NOT_ELIGIBLE_COOKIE_KEY } from "@helpers/constants";
import { ToastrService } from "ngx-toastr";
import { TranslationService } from "@core-services/translation.service";
import { UserService } from "@main-services/user.service";
import { Router } from "@angular/router";

@Injectable({
	providedIn: "root"
})
export class OrdersEntryService {
	private readonly navigationService = inject(LimitOrderNavigationFacade);
	private readonly checkoutNavigation = inject(CheckoutNavigationService);
	private readonly tradableListService = inject(TradableListService);
	private readonly fiatWalletService = inject(FiatWalletService);
	private readonly accountService = inject(AccountService);
	private readonly walletListService = inject(TradableWalletListService);
	private readonly tooltipService = inject(TooltipService);
	private readonly fiatService = inject(FiatService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly legacyTradeEntryService = inject(LegacyTradeEntryService);
	private readonly modalService = inject(ModalService);
	private readonly newModalService = inject(UniversalModalService);
	private readonly taxesFacade = inject(TaxesFacade);
	private readonly toastr = inject(ToastrService);
	private readonly translationService = inject(TranslationService);
	private readonly userService = inject(UserService);
	private readonly router = inject(Router);

	public openOrderFlow(orderType: OrderType, assetId?: string): void {
		if (orderType === OrderType.Buy) {
			this.openBuyOrderFlow(assetId);
		}
		if (orderType === OrderType.Sell) {
			this.openSellOrderFlow(assetId);
		}
	}

	public openBuyOrderFlow(assetId?: string, queryParamObject?: Partial<OrdersQueryParamsCollection>): void {
		if (this.handleUnverifiedAndBusinessCases()) {
			return;
		}
		if (assetId) {
			this.openAmountScreen(assetId, queryParamObject);
		} else {
			this.openAssetSelection();
		}
	}

	private openAmountScreen(assetId: string, queryParamObject?: Partial<OrdersQueryParamsCollection>): void {
		const tradable = this.tradableListService.getItemById(assetId);
		const fiatId = this.accountService.account?._fiat_id;
		const fiatWalletId = this.fiatWalletService.getWalletByFiatId(fiatId)?._id;
		let walletId = "";
		const defaultWallet = this.walletListService
			.getWalletsByTradableId(assetId)
			.find((wallet) => wallet.isDefault());

		// Limit Orders Only support default wallets now => replace defaultWallet.getId with queryParamObject?.WalletId to enable multi-wallet
		// Set all query params to default values if they are not provided
		let qry: Partial<OrdersQueryParamsCollection> = {
			[LimitOrderQueryParamName.FiatId]: queryParamObject?.FiatId ?? fiatId,
			[LimitOrderQueryParamName.AssetId]: queryParamObject?.AssetId ?? assetId,
			[LimitOrderQueryParamName.FiatWalletId]: queryParamObject?.FiatWalletId ?? fiatWalletId,
			[LimitOrderQueryParamName.WalletId]: defaultWallet.getId(),
			[LimitOrderQueryParamName.Amount]: queryParamObject?.Amount ?? "0",
			[LimitOrderQueryParamName.TargetPrice]: "0"
		};
		this.navigationService.gotoByNavigation(LimitOrderRouteName.LimitOrderAmount, qry);
		if (qry.WalletId) {
			walletId = qry.WalletId;
			qry = {
				...qry,
				WalletId: walletId
			};
			this.navigationService.gotoByNavigation(LimitOrderRouteName.LimitOrderAmount, qry);
			return;
		}

		// If wallet id is not specified, select one
		let walletList = this.availableWallets("buy", tradable, this.fiatService.getItemById(fiatId));

		// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
		if (walletList.length > 1) {
			walletList = walletList.filter((w) => !w.isNew());
		}
		// If we still have more than 1 wallet, then we redirect to wallet select
		// Set Optional parameter for checkout flow isPriceOrder
		if (walletList.length > 1) {
			const checkoutQry: Partial<CheckoutQueryParamsCollection> = {
				[CheckoutQueryParamName.AssetId]: qry.AssetId,
				[CheckoutQueryParamName.WalletGuid]: "",
				[CheckoutQueryParamName.FiatId]: qry.FiatId,
				[CheckoutQueryParamName.FiatWalletId]: qry.FiatWalletId,
				[CheckoutQueryParamName.selectedAmountType]: AmountType.fiat,
				[CheckoutQueryParamName.paymentMethod]: PaymentMethod.fiatwallet,
				[CheckoutQueryParamName.paymentMethodId]: "none",
				[CheckoutQueryParamName.BuyAmount]: 0,
				[CheckoutQueryParamName.IsPriceOrder]: true
			};
			this.checkoutNavigation.gotoByNavigation(CheckoutRouteName.AssetWallets, {
				...checkoutQry
			});
			return;
		}
		walletId = walletList[0]._id;

		qry = {
			...qry,
			WalletId: walletId
		};
		this.navigationService.gotoByNavigation(LimitOrderRouteName.LimitOrderAmount, qry);
	}

	public openAssetSelection(isMaxOrdersReached: boolean = false): void {
		const isLimitReached = isMaxOrdersReached ? null : true;
		this.launchDarklyService
			.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED)
			.pipe(take(1))
			.subscribe((isCheckoutFlowEnabled) => {
				if (isCheckoutFlowEnabled) {
					this.checkoutNavigation.gotoByNavigation(CheckoutRouteName.AssetSelect, {
						[CheckoutQueryParamName.IsPriceOrder]: isLimitReached
					});
				} else {
					this.legacyTradeEntryService.openLegacyBuy(isLimitReached);
				}
			});
	}

	private availableWallets(action: string, tradable: Tradable, fiat: Fiat): TradableWallet[] {
		// Limit Orders Only support default wallets now => removing wallet.isDefault() enables other on FE
		return this.walletListService.getWalletsForTradable(tradable.getId()).filter((wallet) => {
			if (action === "sell") {
				return !this.tooltipService.isDisabled(action, tradable, fiat, wallet);
			}
			return wallet.isDefault() && !this.tooltipService.isDisabled(action, tradable, fiat, wallet);
		});
	}

	public openSellOrderFlow(
		assetId?: string,
		queryParamObject?: Partial<OrdersQueryParamsCollection>,
		skipTaxesCheck?: boolean
	): void {
		if (this.handleUnverifiedAndBusinessCases()) {
			return;
		}
		if (assetId) {
			this.openSellAmount(assetId, queryParamObject, skipTaxesCheck);
		} else {
			this.legacyTradeEntryService.openLegacySell(true);
		}
	}

	private openSellAmount(
		assetId?: string,
		queryParamObject?: Partial<OrdersQueryParamsCollection>,
		skipTaxesCheck?: boolean
	): void {
		const tradable = this.tradableListService.getItemById(assetId);
		const fiatId = this.accountService.account?._fiat_id;
		const fiatWalletId = this.fiatWalletService.getWalletByFiatId(fiatId)?._id;
		let walletId = "";

		// Set all query params to default values if they are not provided
		let qry: Partial<OrdersQueryParamsCollection> = {
			[LimitOrderQueryParamName.FiatId]: queryParamObject?.FiatId ?? fiatId,
			[LimitOrderQueryParamName.AssetId]: queryParamObject?.AssetId ?? assetId,
			[LimitOrderQueryParamName.FiatWalletId]: queryParamObject?.FiatWalletId ?? fiatWalletId,
			[LimitOrderQueryParamName.WalletId]: queryParamObject?.WalletId,
			[LimitOrderQueryParamName.Amount]: queryParamObject?.Amount ?? "0",
			[LimitOrderQueryParamName.TargetPrice]: "0",
			[LimitOrderQueryParamName.OrderType]: OrderType.Sell
		};

		// If wallet is pre-set
		if (qry.WalletId) {
			walletId = qry.WalletId;
			qry = {
				...qry,
				WalletId: walletId
			};
			// Tax withholding for pre-selected wallet
			this.handleTaxStatusForTrade(walletId, assetId, qry, skipTaxesCheck);
			return;
		}

		// If wallet id is not specified, select one
		let walletList = this.availableWallets("sell", tradable, this.fiatService.getItemById(fiatId));

		// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
		if (walletList.length > 1) {
			walletList = walletList.filter((w) => !w.isNew());
		}
		// If we still have more than 1 wallet, then we redirect to wallet select
		// Set Optional parameter for checkout flow isPriceOrder
		if (walletList.length > 1) {
			const selectionModal: Modal<WalletSelectionModalComponent> = this.modalService.create(
				WalletSelectionModalComponent,
				{
					tradableWallets: walletList,
					tradableId: assetId
				}
			);
			this.newModalService.close(ModalCloseReason.close);
			selectionModal.present();
			selectionModal.onDismiss.subscribe((res) => {
				const selectedWallet: TradableWallet = res["data"];
				if (!selectedWallet) {
					return;
				}
				walletId = selectedWallet.getId();
				// Tax withholding for multi wallet selected wallet
				this.handleTaxStatusForTrade(walletId, assetId, qry, skipTaxesCheck);
			});
		} else {
			walletId = walletList[0]._id;
			// Tax withholding for default wallet when it's not pre-selected
			this.handleTaxStatusForTrade(walletId, assetId, qry, skipTaxesCheck);
		}
	}

	private handleTaxStatusForTrade(
		walletId: string,
		assetId: string,
		qry: Partial<OrdersQueryParamsCollection>,
		canSkipTaxesCheck: boolean = false
	): void {
		this.taxesFacade
			.fetchTaxStatusForTrade$(walletId)
			.pipe(catchError((error) => of("error")))
			.subscribe({
				next: (walletStatus) => {
					const walletById = this.walletListService.getItemById(walletId);
					const assetType = this.tradableListService
						.getItemById(walletById.getCurrencyId())
						.getCurrencyType();
					if (
						walletStatus === "error" &&
						(assetType === CRYPTO_TYPE || assetType === INDEX_TYPE) &&
						Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) !== "true" &&
						!canSkipTaxesCheck
					) {
						this.toastr.error(this.translationService.translate(ToastMessages.Error));
						return;
					}
					if (
						((walletStatus as TaxesUserStatusModel).openDeclarationsCount > 0 ||
							(walletStatus as TaxesUserStatusModel).taxStatus === SelfCertificationModel.Unconfirmed) &&
						(assetType === CRYPTO_TYPE || assetType === INDEX_TYPE) &&
						!canSkipTaxesCheck
					) {
						const queryParams: Partial<TaxesQueryParamsCollection> = {
							assetId: assetId,
							assetWalletId: walletId,
							fromTradeFlow: TaxesRouteNames.SellOrderTrade
						};
						this.taxesFacade.openCryptoDeclarationTradeModal(
							queryParams,
							walletStatus as TaxesUserStatusModel
						);
						return;
					}
					qry = {
						...qry,
						[LimitOrderQueryParamName.WalletId]: walletId
					};
					this.navigationService.gotoByNavigation(LimitOrderRouteName.LimitOrderSellAmount, qry);
					return;
				}
			});
	}

	public handleUnverifiedAndBusinessCases(): boolean {
		if (this.handleUnverifiedRedirection()) {
			return true;
		}
		if (this.userService.isWaitingForDocuments()) {
			this.handleUnacceptedBusinessUser();
			return true;
		}
		return false;
	}

	public handleUnverifiedRedirection(): boolean {
		if (this.userService.isUnverified) {
			this.redirectToVerification();
			return true;
		}
		return false;
	}

	private redirectToVerification(): void {
		this.router.navigate([{ outlets: { modal: ["payments", "user-verification"] } }]);
	}

	private handleUnacceptedBusinessUser(): void {
		this.toastr.error(this.translationService.translate("business_user_not_accepted"));
	}
}
